
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

interface PackageData {
  id: string;
  title: string;
  description: string;
  price: number;
  visibility: "public" | "subscription";
  recipes: string[];
  created_at: string;
  profiles: {
    username: string;
  };
}

const Package = () => {
  const { id: packageId } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      if (!packageId) {
        toast.error("Package ID is required");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('packages')
          .select('*, profiles(username)')
          .eq('id', packageId)
          .single();

        if (error) {
          console.error("Error fetching package:", error);
          toast.error("Failed to load package");
          navigate("/recipes");
          return;
        }

        if (data.visibility === "public" || data.visibility === "subscription") {
          setPackageData(data as PackageData);
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("Failed to load package");
        navigate("/recipes");
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
  }, [packageId, navigate]);

  const handleEdit = () => {
    navigate(`/edit-package/${packageId}`);
  };

  if (loading) {
    return <div>Loading package...</div>;
  }

  if (!packageData) {
    return <div>Package not found</div>;
  }

  return (
    <>
      <Header />
      <div className="container max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-4">{packageData.title}</h1>
        <p className="text-gray-600 mb-4">{packageData.description}</p>
        <p className="mb-2">
          <strong>Price:</strong> ${packageData.price}
        </p>
        <p className="mb-2">
          <strong>Visibility:</strong> {packageData.visibility}
        </p>
        <p className="mb-2">
          <strong>Created At:</strong> {new Date(packageData.created_at).toLocaleDateString()}
        </p>
        {packageData.profiles && (
          <p className="mb-2">
            <strong>Created by:</strong> {packageData.profiles.username}
          </p>
        )}
        {packageData.recipes && packageData.recipes.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mt-4 mb-2">Recipes Included:</h2>
            <ul>
              {packageData.recipes.map((recipeId) => (
                <li key={recipeId}>{recipeId}</li>
              ))}
            </ul>
          </>
        )}
        <button onClick={handleEdit} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-4">
          Edit Package
        </button>
      </div>
      <Footer />
    </>
  );
};

export default Package;
